import { useTranslation } from '@dreamstack/i18n'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import { RouterOutputs, trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import { normalizeHref } from '@dreamstack/util/src/normalizeHref'
import { Popover } from '@headlessui/react'
import { motion, useAnimation } from 'framer-motion'
import map from 'lodash/map'
import some from 'lodash/some'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { IS_ACCENTRO_WEB } from '../config/config'
import { useComingSoonSlug } from '../hooks/useComingSoonSlug'
import { useLexiconSlug } from '../hooks/useLexiconSlug'
import { useNavbarDisplaySettings } from '../hooks/useNavbarDisplaySettings'
import { useProjectsSlug } from '../hooks/useProjectsSlug'
import { useScrollDirection } from '../hooks/useScrollDirection'
import { StyledNavText, StyledSubNavText } from './NavBar'

type StyledNavEntryProps = React.ComponentProps<'a'>
export const StyledNavEntry = forwardRef<
  HTMLAnchorElement,
  StyledNavEntryProps
>(({ className, children, ...rest }, ref) => (
  <a
    ref={ref}
    className={cn(
      'px-px-32 py-px-12 block cursor-pointer hover:bg-[rgba(255,255,255,0.05)]',
      className
    )}
    {...rest}
  >
    {children}
  </a>
))

StyledNavEntry.displayName = 'StyledNavEntry'

export const MotionPanel = motion.create(Popover.Panel)

type StyledMotionPanelProps = React.ComponentProps<typeof MotionPanel> & {
  alignRight?: boolean
}

const StyledMotionPanel = ({
  alignRight,
  className,
  ...rest
}: StyledMotionPanelProps) => (
  <MotionPanel
    className={cn(
      'absolute w-[296px] z-10',
      alignRight && 'right-0',
      className
    )}
    {...rest}
  />
)

export const ExtraNavbarEntry: FunctionComponent<
  React.PropsWithChildren<{
    slug: string
    title: string
  }>
> = ({ slug, title }) => {
  const { asPath } = useRouter()
  return (
    <Link href={`/${slug}`} passHref legacyBehavior>
      <StyledNavEntry className="group">
        <StyledSubNavText isActive={asPath.endsWith(slug)}>
          {title}
        </StyledSubNavText>
      </StyledNavEntry>
    </Link>
  )
}
//this needs to be in a separate component, otherwise there is an undefined if we click on the link
export const ExtraNavbarEntryToInternational: FunctionComponent<
  React.PropsWithChildren<{
    href: string
    title: string
  }>
> = ({ href, title }) => {
  return (
    <Link href={`${href}`} passHref legacyBehavior>
      <StyledNavEntry className="group">
        <StyledSubNavText isActive={false}>{title}</StyledSubNavText>
      </StyledNavEntry>
    </Link>
  )
}

// TODO: typings.
// Hover related stuff inspired by https://codesandbox.io/s/d8llw?file=/src/App.js
export const EntryWithSubNavbar: FunctionComponent<
  React.PropsWithChildren<{
    entry: RouterOutputs['cms']['getNavEntries'][number]
    alignRight?: boolean
    preNode?: React.ReactNode
    postNode?: React.ReactNode
    shouldRoute?: boolean
  }>
> = ({ entry, alignRight, preNode, postNode, shouldRoute = true }) => {
  assertPopulatedDeep(entry)

  const { asPath } = useRouter()
  const { isScrollingDown } = useScrollDirection()
  const t = useTranslation()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [openState, setOpenState] = useState(false)
  const { data: comingSoonProjects } =
    trpc.project.getComingSoonProjects.useQuery(
      {
        locale: 'de',
      },
      {
        trpc: { ssr: true },
        staleTime: Infinity,
        enabled: IS_ACCENTRO_WEB,
      }
    )
  const comingSoonProjectsExist =
    !!comingSoonProjects && comingSoonProjects?.length > 0

  useEffect(() => {
    setOpenState(false)
  }, [isScrollingDown])

  const onHover = (action: 'onMouseEnter' | 'onMouseLeave') => {
    if (!isMobile) {
      if (action === 'onMouseEnter') setOpenState(true)
      if (action === 'onMouseLeave') setOpenState(false)
    }
  }

  const handleClick = () => {
    setOpenState((openState) => !openState)
  }

  // TODO: Fix typing for click event.
  const handleClickOutside = (event: any) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target)) {
      event.stopPropagation()
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown_' + entry.label, handleClickOutside)

    return () => {
      document.removeEventListener(
        'mousedown_' + entry.label,
        handleClickOutside
      )
    }
  })

  const controls = useAnimation()

  useEffect(() => {
    const animate = async () => {
      if (openState) {
        controls.start('displayed')
      } else {
        await controls.start('fadeOut')
        await controls.start('hidden')
      }
    }
    animate()
  }, [openState, controls])
  const entrySlug = entry?.staticPage?.slug

  const isActive =
    !!entrySlug &&
    (asPath.endsWith(entrySlug) ||
      some(entry?.subEntries, (item) => {
        const itemSlug = item?.staticPage?.slug
        return itemSlug && asPath.endsWith(itemSlug)
      }))

  const {
    displayDefaultSubEntry,
    displayProjectsSubEntry,
    displayLexiconSubEntry,
  } = useNavbarDisplaySettings(entry)
  const projectsSlug = useProjectsSlug()
  const lexiconSlug = useLexiconSlug()
  const comingSoonSlug = useComingSoonSlug()
  const finalHref = normalizeHref({
    href: entrySlug ? `/${entrySlug}` : entry?.url,
  })
  return (
    <Popover className="relative focus-visible:outline-none">
      {({ open }) => (
        <div
          onMouseEnter={() => onHover('onMouseEnter')}
          onMouseLeave={() => onHover('onMouseLeave')}
        >
          <Popover.Button
            ref={buttonRef}
            className="font-semibold focus-visible:outline-none cursor-default"
            onClick={() => handleClick()}
          >
            {shouldRoute ? (
              <Link href={finalHref} passHref legacyBehavior>
                <a className="cursor-default">
                  <StyledNavText isActive={isActive}>
                    {entry.label}
                  </StyledNavText>
                </a>
              </Link>
            ) : (
              <a className="cursor-default">
                <StyledNavText isActive={isActive}>{entry.label}</StyledNavText>
              </a>
            )}
          </Popover.Button>
          <StyledMotionPanel
            alignRight={alignRight}
            key={'parentPanel_' + entry.contentfulId}
            static
            initial="hidden"
            animate={controls}
            variants={{
              fadeOut: {
                opacity: 0,
                translateY: '10px',
                transition: {
                  duration: 0.15,
                },
              },
              displayed: {
                opacity: 1,
                visibility: 'unset',
                translateY: '0px',
              },
              hidden: {
                visibility: 'hidden',
                translateY: '10px',
              },
            }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="mt-px-20"
              style={{ boxShadow: '0 2px 6px 0 rgba(43, 45, 54, 0.5)' }}
            >
              <div className="bg-accentroGray-full py-px-20">
                {preNode}
                {displayDefaultSubEntry && (
                  <Link href={finalHref} passHref legacyBehavior>
                    <StyledNavEntry className="group">
                      <StyledSubNavText
                        isActive={!!entrySlug && asPath.endsWith(entrySlug)}
                      >
                        {t('accentro:navigation.overview')}
                      </StyledSubNavText>
                    </StyledNavEntry>
                  </Link>
                )}
                {map(entry?.subEntries, (subEntry) => {
                  const isActive =
                    !!subEntry?.staticPage?.slug &&
                    asPath.endsWith(subEntry?.staticPage?.slug)
                  const finalHref = normalizeHref({
                    href: subEntry?.staticPage?.slug
                      ? `/${subEntry?.staticPage?.slug}`
                      : subEntry?.url,
                  })
                  return (
                    <Link
                      key={subEntry?.id}
                      href={finalHref}
                      passHref
                      legacyBehavior
                    >
                      <StyledNavEntry className="group">
                        <StyledSubNavText isActive={isActive}>
                          {subEntry?.label}
                        </StyledSubNavText>
                      </StyledNavEntry>
                    </Link>
                  )
                })}
                {displayProjectsSubEntry && (
                  <>
                    <ExtraNavbarEntry
                      slug={projectsSlug}
                      title={t('accentro:navigation.projects')}
                    />
                    {comingSoonProjectsExist && (
                      <ExtraNavbarEntry
                        slug={comingSoonSlug}
                        title={t('accentro:navigation.comingSoon')}
                      />
                    )}
                  </>
                )}
                {displayLexiconSubEntry && (
                  <ExtraNavbarEntry
                    slug={lexiconSlug}
                    title={t('accentro:navigation.lexicon')}
                  />
                )}
                {postNode}
              </div>
            </div>
          </StyledMotionPanel>
        </div>
      )}
    </Popover>
  )
}
